import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './NotificationsPage.css';

export default function NotificationsPage() {
  const { t, lang } = useLanguage();
  const { user, notifications, markAllRead, unreadCount, isPro, hasProAccess } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [tab, setTab]                       = useState('inbox');
  const [conversations, setConversations]   = useState([]);
  const [activeConvo, setActiveConvo]       = useState(null);
  const [messages, setMessages]             = useState([]);
  const [newMessage, setNewMessage]         = useState('');
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loading, setLoading]               = useState(true);
  const messagesEndRef = useRef(null);
  const autoOpenedRef  = useRef(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchConversations();
    const channel = supabase
      .channel('notif-page-messages')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, () => {
        fetchConversations();
        if (activeConvo) fetchMessages(activeConvo.id);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'conversations' }, () => {
        fetchConversations();
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user?.id]);

  useEffect(() => {
    if (!activeConvo) return;
    const fresh = conversations.find(c => c.id === activeConvo.id);
    if (fresh && (fresh.buyer_unread !== activeConvo.buyer_unread || fresh.agent_unread !== activeConvo.agent_unread || fresh.last_message_at !== activeConvo.last_message_at)) {
      setActiveConvo(fresh);
    }
  }, [conversations, activeConvo?.id]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    const convoId = searchParams.get('convo');
    if (!convoId || conversations.length === 0 || autoOpenedRef.current) return;
    const target = conversations.find(c => String(c.id) === convoId);
    if (target) { autoOpenedRef.current = true; openConvo(target); }
  }, [conversations]);

  const fetchConversations = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('conversations')
      .select(`*, listings(id, title, city, status), buyer:profiles!conversations_buyer_id_fkey(id, first_name, last_name, full_name, email), agent:profiles!conversations_agent_id_fkey(id, first_name, last_name, full_name, email)`)
      .or(`buyer_id.eq.${user.id},agent_id.eq.${user.id}`)
      .order('last_message_at', { ascending: false });
    if (data) {
      setConversations(data);
      const total = data.reduce((sum, c) => sum + (c.buyer_id === user.id ? (c.buyer_unread || 0) : (c.agent_unread || 0)), 0);
      setUnreadMessages(total);
    }
    setLoading(false);
  };

  const fetchMessages = async (convoId) => {
    const { data } = await supabase
      .from('messages')
      .select('*, sender:profiles!messages_sender_id_fkey(id, first_name, last_name, full_name, email)')
      .eq('conversation_id', convoId)
      .order('created_at', { ascending: true });
    if (data) setMessages(data);
  };

  const openConvo = async (convo) => {
    setActiveConvo(convo);
    await fetchMessages(convo.id);
    const field = convo.buyer_id === user.id ? 'buyer_unread' : 'agent_unread';
    await supabase.from('conversations').update({ [field]: 0 }).eq('id', convo.id);
    fetchConversations();
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConvo) return;
    const content = newMessage.trim();
    setNewMessage('');
    await supabase.from('messages').insert({
      conversation_id: activeConvo.id,
      sender_id: user.id,
      content,
    });
    const isBuyer = activeConvo.buyer_id === user.id;
    await supabase.from('conversations').update({
      last_message: content,
      last_message_at: new Date().toISOString(),
      is_pro_sender: isPro || hasProAccess,
      [isBuyer ? 'agent_unread' : 'buyer_unread']: (isBuyer ? (activeConvo.agent_unread || 0) : (activeConvo.buyer_unread || 0)) + 1,
    }).eq('id', activeConvo.id);
    fetchMessages(activeConvo.id);
    fetchConversations();
  };

  const resolveName = (p, fallback) =>
    p?.full_name?.trim() ||
    [p?.first_name, p?.last_name].filter(Boolean).join(' ').trim() ||
    p?.first_name ||
    p?.email ||
    fallback;

  const getOther = (convo) => {
    if (!user) return '';
    const isBuyer = convo.buyer_id === user.id;
    return resolveName(isBuyer ? convo.agent : convo.buyer, isBuyer ? 'Agent' : 'Buyer');
  };

  const getOtherFull = (convo) => getOther(convo);

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts); const now = new Date(); const diff = now - d;
    if (diff < 60000) return t('notifications.justNow');
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return d.toLocaleDateString(lang === 'el' ? 'el-GR' : 'en-GB', { day: 'numeric', month: 'short' });
  };

  const NotifIcon = ({ icon }) => {
    if (icon === 'welcome') return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
    if (icon === 'bell') return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
    if (icon === 'star') return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
    if (icon === 'pin') return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
    return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
  };

  return (
    <div className="np-page">
      <div className="np-topbar">
        <button className="np-back-btn" onClick={() => navigate(-1)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          {t('notifications.back')}
        </button>
        <div className="np-topbar-center">
          <h1 className="np-topbar-title">{t('notifications.pageTitle')}</h1>
          {(unreadMessages + unreadCount) > 0 && (
            <span className="np-topbar-badge">{unreadMessages + unreadCount}</span>
          )}
        </div>
        <div className="np-topbar-right" />
      </div>

      <div className="np-body">
        {/* Left sidebar */}
        <div className="np-sidebar">
          <div className="np-tabs">
            <button className={`np-tab ${tab === 'inbox' ? 'np-tab--active' : ''}`} onClick={() => { setTab('inbox'); setActiveConvo(null); }}>
              {t('notifications.inbox')}
              {unreadMessages > 0 && <span className="np-tab__badge">{unreadMessages}</span>}
            </button>
            {/* Alerts tab hidden — uncomment to re-enable.
            <button className={`np-tab ${tab === 'alerts' ? 'np-tab--active' : ''}`} onClick={() => setTab('alerts')}>
              {t('notifications.alerts')}
              {unreadCount > 0 && <span className="np-tab__badge">{unreadCount}</span>}
            </button>
            */}
          </div>

          {tab === 'inbox' && (
            <div className="np-list">
              {loading ? (
                <div className="np-loading">
                  <div className="np-loading__spinner" />
                  <span>{t('notifications.loadingConvos')}</span>
                </div>
              ) : conversations.length === 0 ? (
                <div className="np-empty">
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  <p>{t('notifications.noConvos')}</p>
                  <span>{t('notifications.noConvosSub')}</span>
                  <button onClick={() => navigate('/listings')}>{t('notifications.browseListings')}</button>
                </div>
              ) : conversations.map(c => {
                const other = getOther(c);
                const otherFull = getOtherFull(c);
                const unread = c.buyer_id === user?.id ? (c.buyer_unread || 0) : (c.agent_unread || 0);
                const isActive = activeConvo?.id === c.id;
                const isProConvo = c.is_pro_sender;
                return (
                  <div
                    key={c.id}
                    className={`np-convo ${unread > 0 ? 'np-convo--unread' : ''} ${isActive ? 'np-convo--active' : ''} ${isProConvo ? 'np-convo--pro' : ''}`}
                    onClick={() => openConvo(c)}
                  >
                    <div className="np-convo__avatar-wrap">
                      <div className="np-convo__avatar">{other.charAt(0).toUpperCase()}</div>
                    </div>
                    <div className="np-convo__body">
                      <div className="np-convo__top">
                        <div className="np-convo__name-row">
                          <span className="np-convo__name">{otherFull}</span>
                          {isProConvo && <span className="np-pro-pill">Pro</span>}
                        </div>
                        <span className="np-convo__time">{formatTime(c.last_message_at)}</span>
                      </div>
                      <div className="np-convo__listing">{c.listings?.title}</div>
                      <div className="np-convo__preview">{c.last_message || t('notifications.noMessagesYet')}</div>
                    </div>
                    {unread > 0 && <div className="np-convo__dot" />}
                  </div>
                );
              })}
            </div>
          )}

          {tab === 'alerts' && (
            <div className="np-list">
              {unreadCount > 0 && (
                <div className="np-alerts-header">
                  <button className="np-mark-read" onClick={markAllRead}>{t('notifications.markAllRead')}</button>
                </div>
              )}
              {notifications.length === 0 ? (
                <div className="np-empty">
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  <p>{t('notifications.noAlerts')}</p>
                  <span>{t('notifications.noAlertsSub')}</span>
                </div>
              ) : notifications.map(n => (
                <div
                  key={n.id}
                  className={`np-alert ${n.unread ? 'np-alert--unread' : ''}`}
                  onClick={() => n.propertyId && navigate(`/property/${n.propertyId}`)}
                  style={n.propertyId ? {cursor:'pointer'} : {}}
                >
                  <div className="np-alert__icon"><NotifIcon icon={n.icon} /></div>
                  <div className="np-alert__body">
                    <div className="np-alert__title">{n.title}</div>
                    <div className="np-alert__desc">{n.desc}</div>
                    <div className="np-alert__time">{n.time}</div>
                  </div>
                  {n.unread && <div className="np-alert__dot" />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Chat panel */}
        <div className="np-chat">
          {!activeConvo ? (
            <div className="np-chat-empty">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              <p>{t('notifications.selectConvo')}</p>
              <span>{t('notifications.selectConvoSub')}</span>
            </div>
          ) : (
            <>
              <div className="np-chat-header">
                <div className="np-chat-avatar">{getOther(activeConvo).charAt(0).toUpperCase()}</div>
                <div className="np-chat-info">
                  <div className="np-chat-name-row">
                    <span className="np-chat-name">{getOtherFull(activeConvo)}</span>
                    {activeConvo.is_pro_sender && <span className="np-pro-pill np-pro-pill--sm">Pro</span>}
                  </div>
                  <div className="np-chat-sub">{activeConvo.listings?.title} · {activeConvo.listings?.city}</div>
                </div>
              </div>

              {(!activeConvo.listings || activeConvo.listings.status === 'deleted') && (
                <div className="np-removed-banner" role="status">
                  <svg className="np-removed-banner__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span className="np-removed-banner__text">
                    <strong>This listing has been removed.</strong> You can still continue your conversation below.
                  </span>
                </div>
              )}

              <div className="np-messages">
                {messages.length === 0 && (
                  <div className="np-messages-empty">
                    <span>{t('notifications.sayHello')}</span>
                  </div>
                )}
                {(() => {
                  let lastMineIdx = -1;
                  for (let j = messages.length - 1; j >= 0; j--) {
                    if (messages[j].sender_id === user?.id) { lastMineIdx = j; break; }
                  }
                  const otherUnread = activeConvo.buyer_id === user?.id
                    ? (activeConvo.agent_unread || 0)
                    : (activeConvo.buyer_unread || 0);
                  return messages.map((m, i) => {
                    const isMe = m.sender_id === user?.id;
                    const prevMsg = messages[i - 1];
                    const showSender = !isMe && (!prevMsg || prevMsg.sender_id !== m.sender_id);
                    const isLastMine = isMe && i === lastMineIdx;
                    const seen = isLastMine && otherUnread === 0;
                    return (
                      <div key={m.id} className={`np-msg ${isMe ? 'np-msg--me' : 'np-msg--them'}`}>
                        {!isMe && (
                          <div className="np-msg__avatar">
                            {resolveName(m.sender, 'Buyer').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="np-msg__wrap">
                          {showSender && <div className="np-msg__sender">{resolveName(m.sender, 'Buyer')}</div>}
                          <div className="np-msg__bubble">{m.content}</div>
                          <div className="np-msg__time">
                            {formatTime(m.created_at)}
                            {isLastMine && (
                              <span className={`np-msg__status ${seen ? 'np-msg__status--seen' : ''}`}>
                                {seen ? (
                                  <>
                                    <svg width="14" height="9" viewBox="0 0 14 9" fill="none"><path d="M1 4.5L4 7.5L9 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M5 4.5L8 7.5L13 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                    Seen
                                  </>
                                ) : (
                                  <>
                                    <svg width="10" height="9" viewBox="0 0 10 9" fill="none"><path d="M1 4.5L4 7.5L9 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                    Delivered
                                  </>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
                <div ref={messagesEndRef} />
              </div>

              <div className="np-chat-input">
                <input
                  type="text"
                  placeholder={t('notifications.typeMessage')}
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
                />
                <button onClick={sendMessage} disabled={!newMessage.trim()}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
